import { useContext, useEffect, useState } from "react";
import { RedeemCode } from "../RedeemCodesView/RedeemCodesView";
import { ErrorContext } from "../../../context/ErrorContext";
import { AuthContext } from "../../../context/AuthContext";
import { Button, Modal } from "react-bootstrap";
import { LicenseType } from "../../../collector";
import Switch from "../../../components/Switch/Switch";
import EnabledLabel from "../../../components/EnabledLabel";

interface EditRedeemCodeProps {
    editedRedeemCode: () => void;
    redeemCodeId: number | null;
    environment: string;
    close: () => void;
}

export default function EditRedeemCodeModal(props: EditRedeemCodeProps) {

    const [redeemCode, setRedeemCode] = useState<RedeemCode | null>(null);
    const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(false);
    const [uniqueCode, setUniqueCode] = useState<boolean>(true);
    const [show, setShow] = useState<boolean>(false);
    const { showErrorModal } = useContext(ErrorContext);
    const { token } = useContext(AuthContext);

    useEffect(() => {
        if (props.redeemCodeId) {
            fetchRedeemCode();
            handleShow();
        }
    }, [props.redeemCodeId])

    const handleClose = () => {
        setRedeemCode(null);
        props.close();
        setShow(false);
    }
    const handleShow = () => setShow(true);

    const editRedeemCode = async () => {
        if (!redeemCode) return;
        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'id': redeemCode.id,
                    'enabled': redeemCode.enabled,
                    'productId': redeemCode.productId,
                    'code': redeemCode.code,
                    'uses': redeemCode.uses,
                    'maxUses': redeemCode.maxUses,
                    'autoGenerateCode': autoGenerateCode,
                    'orderId': redeemCode.orderId === 0 ? null : redeemCode.orderId
                })
            }
            await fetch(props.environment + 'updateRedeemCode', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to edit redeem code");
                    }
                    return res.json();
                })
                .then(() => {
                    props.editedRedeemCode();
                    handleClose();
                });
        } catch (e: any) {
            showErrorModal(e.message ?? "Failed to update redeem code - Server is unavailable at the moment");
        }
    }

    const checkRedeemCode = async () => {
        if (!redeemCode) return;
        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'code': redeemCode.code,
                    'id': redeemCode.id,
                })
            }
            await fetch(props.environment + 'checkRedeemCode', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to check if code is unique");
                    }
                    return res.json();
                })
                .then(({ exists }) => {
                    exists ? setUniqueCode(false) : editRedeemCode();
                });
        } catch (e: any) {
            showErrorModal(e.message ?? "Failed to check if code is unique- Server is unavailable at the moment");
        }
    }

    const fetchRedeemCode = async () => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'id': props.redeemCodeId,
                })
            }
            await fetch(props.environment + 'getRedeemCode', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to fetch redeem code");
                    }
                    return res.json();
                })
                .then(({ redeemCode }) => {
                    setRedeemCode(redeemCode);
                });
        } catch (e: any) {
            showErrorModal(e.message ?? "Failed to fetch redeem code - Server is unavailable at the moment");
            handleClose();
        }
    }

    return (
        <>
            {show && redeemCode &&
                <Modal
                    show={show}
                    backdrop="static"
                    onHide={() => handleClose()}
                    keyboard={false}
                >
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        autoGenerateCode ? editRedeemCode() : checkRedeemCode();
                    }}>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Redeem Code</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="dialog-data-field">
                                <span>Order ID:</span>
                                <input type={'number'} placeholder={'None'} defaultValue={redeemCode.orderId ?? undefined}
                                    onChange={(item) => setRedeemCode({ ...redeemCode, orderId: Number(item.target.value) })}
                                />
                            </div>
                            <div className="dialog-data-field">
                                <span>Product:</span>
                                <select value={redeemCode.productId} required
                                    onChange={(item) => setRedeemCode({ ...redeemCode, productId: Number(item.target.value) })}
                                >
                                    <option value={LicenseType.Pro}>DOTE Pro</option>
                                    <option value={LicenseType.ProCommunity}>DOTE Pro Community</option>
                                    <option value={LicenseType.SiteLicense}>Site-License</option>
                                </select>
                            </div>
                            <div className="dialog-data-field">
                                <span>Uses:</span>
                                <input type={'number'} min={0} value={redeemCode.uses} required
                                    onChange={(item) => setRedeemCode({ ...redeemCode, uses: Number(item.target.value) })}
                                />
                            </div>
                            <div className="dialog-data-field">
                                <span>Max uses:</span>
                                <input type={'number'} min={0} value={redeemCode.maxUses} required
                                    onChange={(item) => setRedeemCode({ ...redeemCode, maxUses: Number(item.target.value) })}
                                />
                            </div>
                            <div className="dialog-data-field" style={{ alignItems: 'center' }}>
                                <span style={{ alignSelf: 'flex-start' }}>Code:</span>
                                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}>
                                    <div style={{ display: 'flex', columnGap: '3px', justifyContent: 'flex-end' }}>
                                        <Button style={{ opacity: (autoGenerateCode ? '1' : '0.5') }} onClick={() => setAutoGenerateCode(!autoGenerateCode)} >
                                            Auto generate new code
                                        </Button>
                                    </div>
                                    {!autoGenerateCode &&
                                        <input placeholder="Write code here..." value={redeemCode.code} required
                                            onChange={(item) => setRedeemCode({ ...redeemCode, code: item.target.value.toLowerCase() })}
                                        />
                                    }
                                </div>
                            </div>
                            {!uniqueCode && <p style={{ color: 'red' }}>Code must be unique, please enter a valid code</p>}
                            <div className="dialog-data-field">
                                <span>Enabled:</span>
                                <Switch checked={redeemCode.enabled} onChange={(item) => setRedeemCode({ ...redeemCode, enabled: item.target.checked })} />
                            </div>
                            <div className="text-end-container">
                                <EnabledLabel enabled={redeemCode.enabled} />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => handleClose()}>
                                Close
                            </Button>
                            <Button variant="primary" type='submit'>Save</Button>
                        </Modal.Footer>
                    </form>
                </Modal>
            }
        </>
    );
}